import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { AssetsConfig } from "@nolimitcity/slot-game/bin/asset/AssetsConfig";
import { ResourcesGroupName } from "@nolimitcity/slot-game/bin/asset/ResourcesGroupName";
import { FlowState } from "@nolimitcity/slot-game/bin/flowcontrol/FlowState";
import { GameFlowController } from "@nolimitcity/slot-game/bin/flowcontrol/GameFlowController";
import { NolimitApplication } from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import { NolimitPlugin } from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import { FontLoader } from "@nolimitcity/slot-launcher/bin/loader/FontLoader";
import { OpenSans } from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import { LogLevel } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { gsap } from "gsap";

import { AbstractSymbol } from "../abstracts/elements/AbstractSymbol";
import { EventsManager, GameEvents } from "../abstracts/events/EventsManager";
import {
    AbstractFlowPresentation,
    setModelForFlowPresentation,
} from "../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../abstracts/flow/AbstractGameMode";
import { setAssetsLoader } from "../abstracts/graphics/AbstractSpine";
import {
    AbstractModuleController,
    setModelForModulesController,
} from "../abstracts/modules/AbstractModuleController";
import { IBetWayWinData } from "../abstracts/types/interfaces";
import { initializeGameLogger } from "../abstracts/utils/AbstractConsole";
import { AbstractTimelineController } from "../abstracts/utils/AbstractTimelineController";
import { playSound } from "../abstracts/utils/SoundUtils";
import { devDebugConfig } from "./UCConfig";
import { UCAssetsLoader } from "./assetsLoader/UCAssetsLoader";
import { UCSoundNames } from "./assetsLoader/UCAssetsNames";
import { UCFreespinBetPresentation } from "./flow/freespin/UCFreespinBetPresentation";
import { UCFreespinIdlePresentation } from "./flow/freespin/UCFreespinIdlePresentation";
import { UCFreespinResultPresentation } from "./flow/freespin/UCFreespinResultPresentation";
import { UCFreespinWinPresentation } from "./flow/freespin/UCFreespinWinPresentation";
import { UCNormalBetPresentation } from "./flow/normal/UCNormalBetPresentation";
import { UCNormalResultPresentation } from "./flow/normal/UCNormalResultPresentation";
import { UCNormalWinPresentation } from "./flow/normal/UCNormalWinPresentation";
import { UCNormalIdlePresentation } from "./flow/normal/UCNormaldlePresentation";
import { UCSuperFreespinBetPresentation } from "./flow/superFreespin/UCSuperFreespinBetPresentation";
import { UCSuperFreespinIdlePresentation } from "./flow/superFreespin/UCSuperFreespinIdlePresentation";
import { UCSuperFreespinResultPresentation } from "./flow/superFreespin/UCSuperFreespinResultPresentation";
import { UCSuperFreespinWinPresentation } from "./flow/superFreespin/UCSuperFreespinWinPresentation";
import { UCModel } from "./model/UCModel";
import { UCBackgroundController } from "./modules/Background/UCBackgroundController";
import UCReel from "./modules/Reels/UCReel";
import { UCReelsController } from "./modules/Reels/UCReelsController";
import { UCBigFishSymbol } from "./modules/Reels/symbols/UCBigFishSymbol";
import { UCMysterySymbol } from "./modules/Reels/symbols/UCMysterySymbol";
import { UCSymbol, isFish } from "./modules/Reels/symbols/UCSymbol";
import { UCWildSymbol } from "./modules/Reels/symbols/UCWildSymbol";
import { UCSoundController } from "./modules/Sound/UCSoundController";
import { UCUserInterfaceController } from "./modules/UserInterface/UCUserInterfaceController";
import { UCWinController } from "./modules/Winnings/UCWinController";
import { UCSymbolNames } from "./types/UCEnums";
import { UCSoundDebugger } from "./utils/UCSoundDebugger";
import { APIEvent } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Skinner} from "@nolimitcity/slot-launcher/bin/utils/Skinner";
export class UgliestCatch extends SlotGame {
    public static model: UCModel;
    public static assetLoader: AssetLoader;
    public static gameFlowController: GameFlowController;

    private skinner:Skinner;
    constructor() {
        super();
        const validSkins:string[] = [
            "sweep"
        ]
        this.skinner = new Skinner(validSkins);
    }

    private handleSweepInit(){
        //Making sure that skinID is set based  on "replaceWordFree" gcc.
        if (SlotGame.apiPlugin.gameClientConfiguration.isSet) {
            if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                this.skinner.skinId = "sweep";
            }
        } else {
            SlotGame.apiPlugin.events.on(APIEvent.GAME_CLIENT_CONFIGURATION_APPLIED, () => {
                if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                    this.skinner.skinId = "sweep";
                }
            })
        }
    }

    private handleSweepGetReady() : Promise<any>{
        if (this.skinner.skinId != undefined) {
            const replaceMap:Map<string, string> = new Map();
            //Bonus
            replaceMap.set("BOOSTED_BET", "BOOSTED_BET.png");
            replaceMap.set("FREESPIN_8_SPINS", "FREESPIN_8_SPINS.png");
            replaceMap.set("page4", "page4.png");
            replaceMap.set("pageImage_6", "pageImage_6.png");
            return this.skinner.loadSkin(replaceMap);
        }
        return Promise.resolve();
    }



    private registerModules(): void {
        let layerIndex: number = 0;

        const addController = (controller: AbstractModuleController) => {
            if (controller.view.isDisabled) {
                return;
            }

            NolimitApplication.addLayerAt(
                controller.name.replace(`Controller`, ``) + `-layer`,
                controller.view,
                layerIndex
            );
            layerIndex++;
        };

        //add them in the order you need them displayed
        addController(new UCBackgroundController());
        addController(new UCReelsController());
        addController(new UCUserInterfaceController());
        addController(new UCWinController());
        addController(new UCSoundController());
    }

    public init(): Promise<NolimitPlugin> {
        return super.init().then((value: NolimitPlugin) => {
            UgliestCatch.model = new UCModel(SlotGame.apiPlugin, true);

            setModelForModulesController(UgliestCatch.model);
            setModelForFlowPresentation(UgliestCatch.model);
            initializeGameLogger(`UgliestCatch`);

            UgliestCatch.gameFlowController = new GameFlowController(
                SlotGame.apiPlugin,
                UgliestCatch.model
            );
            UgliestCatch.gameFlowController.flowStateLogger.level = LogLevel.DEV;

            //IDLE
            const normalIdle = new UCNormalIdlePresentation();
            const freespinIdle = new UCFreespinIdlePresentation();
            const superFreespinIdle = new UCSuperFreespinIdlePresentation();

            //BET
            const normalBet = new UCNormalBetPresentation();
            const freespinBet = new UCFreespinBetPresentation();
            const superFreespinBet = new UCSuperFreespinBetPresentation();

            //RESULT
            const normalResult = new UCNormalResultPresentation();
            const freespinResult = new UCFreespinResultPresentation();
            const superFreespinResult = new UCSuperFreespinResultPresentation();

            //WIN
            const normalWin = new UCNormalWinPresentation();
            const freespinWin = new UCFreespinWinPresentation();
            const superFreespinWin = new UCSuperFreespinWinPresentation();

            UgliestCatch.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.NORMAL,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, normalIdle],
                    [FlowState.BET, normalBet],
                    [FlowState.RESULT, normalResult],
                    [FlowState.WIN, normalWin],
                ])
            );

            UgliestCatch.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.FREESPIN,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, freespinIdle],
                    [FlowState.BET, freespinBet],
                    [FlowState.RESULT, freespinResult],
                    [FlowState.WIN, freespinWin],
                ])
            );

            UgliestCatch.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.SUPER_FREESPIN,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, superFreespinIdle],
                    [FlowState.BET, superFreespinBet],
                    [FlowState.RESULT, superFreespinResult],
                    [FlowState.WIN, superFreespinWin],
                ])
            );

            //xbet
            this.handleSweepInit();
            return value;
        });
    }

    private paytable:string = require("../../nolimit/screens/paytable.mustache");
    public getPaytable(): Promise<string> {
        return new Promise((resolve, reject) => {
            const data = {
                skinPath: "screens/images/"
            };
            if (this.skinner.skinId != undefined) {
                data.skinPath = `../resources/images/${this.skinner.skinId}/`;
            }
            let paytableString: string = (<any>this.paytable).text;
            paytableString = paytableString.replace(/{{skinPath}}/g, data.skinPath);
            resolve(paytableString);
        });
    }

    public getReady(): Promise<NolimitPlugin> {
        //Show intro and load game AssetsModule
        return super.getReady().then((value: NolimitPlugin) => {
            const fontLoader = new FontLoader(NolimitApplication.resourcePath);
            fontLoader.add(OpenSans.ITALIC_600);
            fontLoader.add(OpenSans.NORMAL_300);
            fontLoader.add(OpenSans.NORMAL_400);
            fontLoader.add(OpenSans.NORMAL_700);

            AssetsConfig.addToAssetsConfig(UCAssetsLoader.RESOURCES_GROUPS);
            UgliestCatch.assetLoader = new AssetLoader(
                UgliestCatch.apiPlugin,
                NolimitApplication.resourcePath + `/resources/`
            );
            setAssetsLoader(UgliestCatch.assetLoader);

            const promises = Promise.all([
                fontLoader.load(),
                UgliestCatch.assetLoader.load(ResourcesGroupName.MAIN),
                this.handleSweepGetReady(),
            ]);

            return promises.then(() => {
                this.registerModules();

                devDebugConfig.showTimelineController && new AbstractTimelineController();
                devDebugConfig.showSoundDebugger && new UCSoundDebugger();

                UgliestCatch.apiPlugin.freeBets.events.on(
                    UgliestCatch.apiPlugin.freeBets.FREE_BETS_START,
                    () => {
                        EventsManager.trigger(GameEvents.ON_FREE_BETS_START);

                        UgliestCatch.apiPlugin.freeBets.events.once(
                            UgliestCatch.apiPlugin.freeBets.FREE_BETS_END,
                            () => {
                                EventsManager.trigger(GameEvents.ON_FREE_BETS_END);
                            }
                        );
                    }
                );

                return value;
            });
        });
    }

    public getReadyToStart(): Promise<NolimitPlugin> {
        //Game assets loaded, waiting for user to start game.

        return super.getReadyToStart().then((value: NolimitPlugin) => {
            return value;
        });
    }

    public start(): Promise<NolimitPlugin> {
        playSound({ name: UCSoundNames.CONTINUE_BUTTON });
        //User has pressed continue and the game starts.
        return super.start().then((value: NolimitPlugin) => {
            UgliestCatch.gameFlowController.start();

            return value;
        });
    }

    public getNoWinGameData(): any {
        return {
            reels: [
                [`M`, `L2`, `M2`, `F1`, `M2`],
                [`M5`, `M3`, `M1`, `M5`, `M5`],
                [`M2`, `L3`, `L3`, `F1`, `M1`],
                [`M5`, `L2`, `F1`, `L2`, `M5`],
                [`M3`, `F1`, `F1`, `L3`, `M2`],
            ],
            evaluatedArea: [
                [`X`, `L2`, `M2`, `F1`, `X`],
                [`X`, `M3`, `M1`, `M5`, `X`],
                [`X`, `L3`, `L3`, `F1`, `X`],
                [`X`, `L2`, `F1`, `L2`, `X`],
                [`X`, `F1`, `F1`, `L3`, `X`],
            ],
            betWayWins: [],
            totalSpinWinnings: 0,
            accumulatedRoundWin: 0,
            playedBetValue: 1,
            mode: `NORMAL`,
            nextMode: `NORMAL`,
            numberOfFreespinsPlayed: 0,
            freespinsLeft: 0,
            addedNumberOfFreespinsThisSpin: 0,
            symbolSizes: [
                [1, 1, 1, 1, 1],
                [1, 1, 1, 1, 1],
                [1, 1, 1, 1, 1],
                [1, 1, 1, 1, 1],
                [1, 1, 1, 1, 1],
            ],
            wasFeatureBuy: false,
            brokeTheBank: false,
            fsMultiplier: 1,
            fishermanProgression: 0,
            freespinTriggeredThisSpin: false,
            extraPositionsOpen: [false, false, false, false, false],
            enhancedBet: false,
            preReplaceFishReels: [
                [`M`, `L2`, `M2`, `F`, `M2`],
                [`M5`, `M3`, `M1`, `M5`, `M5`],
                [`M2`, `L3`, `L3`, `F`, `M1`],
                [`M5`, `L2`, `F`, `L2`, `M5`],
                [`M3`, `F`, `F`, `L3`, `M2`],
            ],
        };
    }
    public static getIndiSymWinHighlightTl(
        betWayWinsData: IBetWayWinData[],
        reels: UCReel[]
    ): gsap.core.Timeline {
        const tl = gsap.timeline();
        const winningsArray = UgliestCatch.model.finalWinningPosition;
        if (betWayWinsData.length > 0) {
            betWayWinsData.forEach((betWayData: IBetWayWinData) => {
                const wayTimeline = gsap.timeline({
                    smoothChildTiming: true,
                    autoRemoveChildren: false,
                });
                let sym: any;
                reels.forEach((reel, reelIndex) => {
                    const visibleSymbols: AbstractSymbol[] = reel.getAllVisibleStopSymbols();
                    const reelWinSymbols = reel.getWinningSymbols(winningsArray[reelIndex]);
                    const wayWinSymbols: AbstractSymbol[] = [];
                    reelWinSymbols.forEach((symbol) => {
                        const cond1 = symbol.name === betWayData.symbol;

                        const cond2 =
                            symbol instanceof UCMysterySymbol &&
                            UgliestCatch.model.gameData.evaluatedArea[reelIndex][
                                symbol.positionInArray - 1
                            ] === betWayData.symbol &&
                            winningsArray[reelIndex][symbol.positionInArray - 1];

                        const cond3 =
                            symbol instanceof UCMysterySymbol &&
                            isFish(symbol.revealedSymbolName as UCSymbolNames) &&
                            betWayData.symbol === `M4` &&
                            winningsArray[reelIndex][symbol.positionInArray - 1];

                        const cond4 =
                            symbol.name === UCSymbolNames.WILD &&
                            UgliestCatch.model.finalWinningPosition[reelIndex][
                                symbol.positionInArray - 1
                            ] &&
                            betWayData.length >= reelIndex + 1;

                        const cond5 =
                            symbol instanceof UCWildSymbol &&
                            UgliestCatch.model.finalWinningPosition[reelIndex][
                                symbol.positionInArray - 1
                            ] &&
                            symbol.isGold &&
                            isFish(betWayData.symbol as UCSymbolNames);

                        const cond6 =
                            betWayData.symbol === `M4` && isFish(symbol.name as UCSymbolNames);

                        const cond7 =
                            symbol instanceof UCBigFishSymbol &&
                            symbol.revealedSymbolName === betWayData.symbol;

                        if (cond1 || cond2 || cond3 || cond4 || cond5 || cond6 || cond7) {
                            const symbolWinAnimation = symbol.getWinAnimation();
                            sym = symbol;
                            wayTimeline.add(symbolWinAnimation, 0);
                            wayWinSymbols.push(symbol as UCSymbol);
                        }
                    });

                    visibleSymbols.forEach((symbol) => {
                        if (!wayWinSymbols.includes(symbol as UCSymbol)) {
                            wayTimeline.add((symbol as UCSymbol).getNoWinAnimation(), 0);
                        }
                    });
                });
                if (sym) {
                    wayTimeline.add(sym.getIndiSymWinAmtWaysTl(betWayData), 0);
                }
                tl.add(wayTimeline);
            });
        }
        return tl;
    }
}
